import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, GripVertical, Type, Image, Quote, Heading, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOCK_TYPES = [
  { value: 'heading', label: 'Titulo', icon: Heading },
  { value: 'text', label: 'Texto', icon: Type },
  { value: 'image', label: 'Imagen', icon: Image },
  { value: 'quote', label: 'Cita', icon: Quote },
];

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const token = localStorage.getItem('admin_token');

  useEffect(() => { fetchPages(); }, []);

  const fetchPages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pages`);
      const data = await res.json();
      setPages(data);
      if (data.length > 0 && !selectedSlug) {
        setSelectedSlug(data[0].slug);
        setEditingPage(JSON.parse(JSON.stringify(data[0])));
      }
    } catch (e) { console.error(e); }
  };

  const selectPage = (slug) => {
    const page = pages.find(p => p.slug === slug);
    if (page) {
      setSelectedSlug(slug);
      setEditingPage(JSON.parse(JSON.stringify(page)));
    }
  };

  const savePage = async () => {
    if (!editingPage) return;
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/pages/${editingPage.slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: editingPage.title,
          heroImage: editingPage.heroImage,
          blocks: editingPage.blocks,
        }),
      });
      if (res.ok) {
        setMessage('Guardado correctamente');
        fetchPages();
      } else {
        setMessage('Error al guardar');
      }
    } catch (e) {
      setMessage('Error de conexion');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const addBlock = (type) => {
    if (!editingPage) return;
    const newBlock = {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      type,
      content: '',
      imageUrl: '',
      caption: '',
    };
    setEditingPage({ ...editingPage, blocks: [...editingPage.blocks, newBlock] });
  };

  const updateBlock = (blockId, field, value) => {
    if (!editingPage) return;
    setEditingPage({
      ...editingPage,
      blocks: editingPage.blocks.map(b => b.id === blockId ? { ...b, [field]: value } : b),
    });
  };

  const removeBlock = (blockId) => {
    if (!editingPage) return;
    setEditingPage({
      ...editingPage,
      blocks: editingPage.blocks.filter(b => b.id !== blockId),
    });
  };

  const moveBlock = (index, direction) => {
    if (!editingPage) return;
    const blocks = [...editingPage.blocks];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    [blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]];
    setEditingPage({ ...editingPage, blocks });
  };

  return (
    <div data-testid="admin-pages">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Editor de Paginas</h1>
        {message && (
          <span className={`text-sm px-3 py-1 rounded-full ${message.includes('Error') ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
            {message}
          </span>
        )}
      </div>

      <div className="flex gap-6">
        {/* Page selector sidebar */}
        <div className="w-48 flex-shrink-0 space-y-2">
          {pages.map(p => (
            <button
              key={p.slug}
              onClick={() => selectPage(p.slug)}
              className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                selectedSlug === p.slug
                  ? 'bg-[#50c878] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
              data-testid={`page-tab-${p.slug}`}
            >
              {p.title}
            </button>
          ))}
        </div>

        {/* Editor area */}
        {editingPage && (
          <div className="flex-1 space-y-6">
            {/* Page title and hero */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Titulo de la pagina</label>
                <input
                  type="text"
                  value={editingPage.title}
                  onChange={e => setEditingPage({ ...editingPage, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#50c878] focus:border-transparent"
                  data-testid="page-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Imagen Hero (URL)</label>
                <input
                  type="text"
                  value={editingPage.heroImage || ''}
                  onChange={e => setEditingPage({ ...editingPage, heroImage: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#50c878] focus:border-transparent"
                  data-testid="page-hero-input"
                />
                {editingPage.heroImage && (
                  <img src={editingPage.heroImage} alt="Hero preview" className="mt-2 h-32 rounded-lg object-cover" />
                )}
              </div>
            </div>

            {/* Blocks */}
            <div className="space-y-3">
              {editingPage.blocks.map((block, index) => (
                <div key={block.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid={`block-editor-${block.id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {BLOCK_TYPES.find(t => t.value === block.type)?.label || block.type}
                    </span>
                    <div className="flex-1" />
                    <button onClick={() => moveBlock(index, -1)} disabled={index === 0}
                      className="p-1 hover:bg-gray-100 rounded disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveBlock(index, 1)} disabled={index === editingPage.blocks.length - 1}
                      className="p-1 hover:bg-gray-100 rounded disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeBlock(block.id)}
                      className="p-1 hover:bg-red-50 text-red-400 hover:text-red-600 rounded" data-testid={`remove-block-${block.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {(block.type === 'heading' || block.type === 'text' || block.type === 'quote') && (
                    <textarea
                      value={block.content}
                      onChange={e => updateBlock(block.id, 'content', e.target.value)}
                      rows={block.type === 'text' ? 4 : 2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#50c878] focus:border-transparent resize-y"
                      placeholder={block.type === 'heading' ? 'Titulo...' : block.type === 'quote' ? 'Cita...' : 'Texto del parrafo...'}
                    />
                  )}

                  {block.type === 'image' && (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={block.imageUrl}
                        onChange={e => updateBlock(block.id, 'imageUrl', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#50c878] focus:border-transparent"
                        placeholder="URL de la imagen..."
                      />
                      <input
                        type="text"
                        value={block.caption}
                        onChange={e => updateBlock(block.id, 'caption', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#50c878] focus:border-transparent"
                        placeholder="Pie de foto (opcional)..."
                      />
                      {block.imageUrl && (
                        <img src={block.imageUrl} alt={block.caption} className="h-32 rounded-lg object-cover" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add block buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500 mr-2">Anadir bloque:</span>
              {BLOCK_TYPES.map(t => (
                <button
                  key={t.value}
                  onClick={() => addBlock(t.value)}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:border-[#50c878] hover:text-[#50c878] transition-colors"
                  data-testid={`add-block-${t.value}`}
                >
                  <t.icon className="w-4 h-4" />
                  {t.label}
                </button>
              ))}
            </div>

            {/* Save */}
            <button
              onClick={savePage}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-[#50c878] text-white rounded-lg hover:bg-[#40a0a0] transition-colors disabled:opacity-50"
              data-testid="save-page-button"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {saving ? 'Guardando...' : 'Guardar Pagina'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPages;
